// Removed SubeventType import as it's no longer needed
import {
  ClientEmailFlowSectionKey,
  ClientEmailTemplateSend,
  EmailFlowKey,
  RenderedClientEmail
} from './ClientEmailTemplateTypes'
import {
  Enrollment,
  EnrollmentPaymentStatus,
  EnrollmentUser,
  EnrollmentUserRequest,
  EnrollmentWithPayment,
  HelpDancerLevel,
  PayFor,
  DropinTicket,
  DropinTicketLog,
  DropinTicketStatus,
  PaymentMethod,
  PrivateLessonConfig,
  PrivateLessonOrder,
  Currency,
  UserMetricsData
} from './GrooverTypes'
import { PaymentTransaction } from './PaymentTransactionTypes'
import { EmailService } from './MailingTypes'
import { UserRole } from './User'
import { CommunicationLogEvent } from './CommunicationLogTypes'
import { SuppressionEvent, SuppressionInfo } from './SendgridTypes'
import { Templates, TemplateParamsMap } from './EmailTypes'
import { SlackChannels } from '../enums/SlackChannels'
import type { QualifyingRegistration } from './FreeDropinTicketTypes'
import type { UsedContinuationDiscountActivity, UserEnrolledActivityPeriod } from './DiscountTypes'

export type StripeCheckoutCallbackQueryParams = {
  stripeSuccess?: string // "false" | "true"
}

export type FasadPhaseOneQueryParams = {
  externalUserId?: string
  type: FasadSection
  defaultPrompt?: string
}

export type ExternalSessionQueryParams = {
  externalSessionId: string
  enableLogOutPutToScreen?: string
}

export type GetActivePhasadDescriptions = {
  objectId: string
}

export type GetExternalSessionsQueryParams = Partial<{
  userId: number
  objectId: number
}>
// TODO CREATE TEST, should have a jaguar type for each section

export enum FasadSection {
  OVERVIEW = 'overview',
  INTERIOR = 'interior',
  EXTERIOR = 'exterior',
  AREA = 'area'
}

export enum EmailType {
  PAYMENT_REMINDER = 'payment_reminder',
  REGISTRATION_APPROVED = 'registration_approved',
  PAYMENT_CONFIRMED = 'payment_confirmed',
  REGISTRATION_SAVED_NOT_APPROVED = 'registration_saved_not_approved',
  REGISTRATION_SAVED_FREE = 'registration_saved_free',
  ACTIVITY_APPROVED_USERS_EMAIL = 'activity_approved_users_email'
}

export type VitecLandingQueryParams = {
  currentUserId: string // "HAN3294639236244479A4344C989A43B7AB"
  customerId: string // 'M20260'
  estateId: string // 'OBJ20260_2035692683'
  estateType: string // 'House'
  orderId: string // 'PORBFE9DFEEC6B64DABA8B799FF23078887'
  userId: string // 'HAN3294639236244479A4344C989A43B7AB'
}
export type LoginWithCustomerPageQueryParams = {
  uid: string
}

export type DevIdsQueryParams = Partial<{
  teamId: string
  uid: string
}>

export type SignedInQueryParams = {
  redirectToPlayWithOnboarding?: string
  teamCode?: string
}

export type AllAvailableQueryParams =
  LoginWithCustomerPageQueryParams | DevIdsQueryParams | SignedInQueryParams | SignInFromAdminAppQueryParams

export type CreateCustomTokenForIdTokenRequest = {
  idToken: string
}

export type GetEnrollmentsForUserRequest = {
  idToken: string
}

export type SendEnrollmentEmailRecipient = {
  enrollmentId: string
  userId?: string
}

/** Channel selection for start-reminder style sends (email and/or SMS). */
export type ReminderSendChannels = {
  email: boolean
  sms: boolean
}

export type SendEnrollmentEmailRequest = {
  type: EmailType
  recipients: SendEnrollmentEmailRecipient[]
  context?: object
  /** When true, bypass duplicate-prevention and send even if a recent communication log exists */
  forceResend?: boolean
  /**
   * For ACTIVITY_APPROVED_USERS_EMAIL: which channels to send.
   * When omitted, defaults to `{ email: true, sms: true }` (SMS still gated by client SMS config).
   */
  channels?: ReminderSendChannels
  /**
   * For ACTIVITY_APPROVED_USERS_EMAIL SMS: optional send-time template with
   * @activityName / @registrationLink / @schoolName placeholders.
   * Applies to this call only. Automated reminders ignore it and keep the default.
   */
  smsTemplate?: string
  /** @deprecated Use recipients instead */
  enrollmentId?: string
  /** @deprecated Use recipients instead */
  userId?: string
}

export type SendEnrollmentEmailPaidRequest = {
  enrollmentId: string
  payFor: PayFor
}

export type SendEnrollmentEmailResult = {
  enrollmentId: string
  userId?: string
  success: boolean
  message?: string
}

export type SendEnrollmentEmailResponse = {
  type: EmailType
  results: SendEnrollmentEmailResult[]
}

export type TestReminderEmailType = 'payment_reminder' | 'activity_starts_soon'

export type GetReminderRecipientsForActivityRequest = {
  clientId: string
  activityId: string
  type: TestReminderEmailType
}

export type ReminderRecipient = {
  enrollmentId: string
  userId: string
  name: string
  email: string
  phone?: string
}

export type GetReminderRecipientsForActivityResponse =
  { ok: true; recipients: ReminderRecipient[] } | CloudFunctionErrorResponse

export type GetActivityReminderSchedulePreviewRequest = {
  clientId: string
  /** The activity currently being viewed in Edit Activity — a plain activity, Parentevent, or Subevent. */
  activityId: string
}

/**
 * A single upcoming "Activity starts soon" reminder send, computed for the Edit Activity →
 * Advanced tab preview. `activityId`/`activityName` refer to the specific subevent (or the
 * top-level activity itself, when it has no subevents).
 */
export type ReminderSchedulePreviewEntry = {
  activityId: string
  activityName: string
  /** YYYY-MM-DD; the occasion date this reminder is about. */
  occasionDate: string
  /** HH:mm start time of the occasion (Activity.time), if set. */
  occasionTime?: string
  /** YYYY-MM-DD; the date the reminder is scheduled to be sent. */
  sendDate: string
  /** Number of days before occasionDate this reminder fires (the matching activityStartsSoonDaysBeforeList entry). */
  dayOffset: number
  /** Recipients who would still receive this reminder (already-reminded users excluded). */
  recipientCount: number
}

export type GetActivityReminderSchedulePreviewResponse =
  | {
      ok: true
      entries: ReminderSchedulePreviewEntry[]
      truncated: boolean
      /** HH:mm the daily automated-reminders scheduler fires at (Europe/Stockholm) — same for every entry. */
      sendTime: string
    }
  | CloudFunctionErrorResponse

export type SendPrivateLessonsPaymentSuccessEmailRequest = {
  clientId: string
  userEmail: string
  userName: string
  registerToken: string
}

export type SendPrivateLessonsPaymentSuccessEmailResponse =
  | {
      ok: true
      messageId: string
    }
  | CloudFunctionErrorResponse

type SendEmailBaseParams = {
  clientId: string
  enrollmentUserId?: string
}

type SendEmailChannelEmail<T extends Templates = Templates> = {
  channel?: 'email' | undefined
  templateId: T
  to: string
  registerToken?: string
  templateParams: TemplateParamsMap[T]
}

type SendEmailChannelSms = {
  channel: 'sms'
  smsEvent: CommunicationLogEvent
  /** When present, the SMS deep-links to this specific walk-in receipt. */
  walkInPaymentRef?: string
}

export type SendEmailRequest<T extends Templates = Templates> = SendEmailBaseParams &
  (SendEmailChannelEmail<T> | SendEmailChannelSms)

export type SendEmailResponse = { ok: true; messageId: string } | CloudFunctionErrorResponse

export type GetPrivateLessonConfigRequest = {
  clientId: string
}

export type GetPrivateLessonConfigResponse = PrivateLessonConfig

export type GetPrivateLessonOrderRequest = {
  clientId: string
  orderId: string
}

export type GetPrivateLessonOrderResponse = PrivateLessonOrder | null

export type CreatePrivateLessonOrderRequest = {
  clientId: string
  packageId: string
  enrollmentUserId: string
  enrollmentUserName: string
  instructorId?: string
  instructorName?: string
  locationId?: string
  locationName?: string
  registrationComment?: string
  paymentMethod: PaymentMethod
  firstLesson?: {
    startDate: string
    endDate: string
    instructorId?: string
    locationId?: string
  }
  honeypot?: string // Spam protection - should always be empty for legitimate users
}

export type CreatePrivateLessonOrderResponse = {
  orderId: string
  lessonId?: string
}

export type ObfuscatedEnrollmentRequest = {
  enrollmentId: string
  /**
   * Optional viewer scoping for the public registration flow.
   * When provided (and the client feature toggle is enabled), the backend will return a single-person view
   * for this enrollment user (hides other participant, filters payments/priceDetails/receipts).
   */
  enrollmentUserId?: string
}

export type ObfuscatedGroupMember = {
  enrollmentId: string
  userId: string
  displayName: string
  unpaidAmount: number
  paymentStatus: EnrollmentPaymentStatus
}

export type GetObfuscatedEnrollmentResponse = {
  enrollment: EnrollmentWithPayment
  groupMembers?: ObfuscatedGroupMember[]
}

export type GetEnrollmentDataRequest = {
  enrollmentId: string
}

export type GetEnrollmentDataResponse = {
  enrollment: Enrollment
}

export type GetActivityPublicCapacityRequest = {
  activityId: string
}

export type GetActivityPublicCapacityResponse = {
  activityId: string
  seatedCount: number
  maxAllowedParticipants: number
  isAtOrOverCapacity: boolean
}

export type PayForGroupSplit = {
  enrollmentId: string
  userId: string
  amount: number
}

/**
 * Group "Pay for both" settlement data frozen at intent-creation time and
 * stored on the provider's own payment-status doc (Swish/Vipps/Epassi — Stripe
 * uses PaymentIntent metadata instead). The webhook reads this back to fan out
 * one `Payment` per enrollment after settlement.
 */
export type PayForGroupSettlement = {
  enrollmentGroupId: string
  splits: PayForGroupSplit[]
}

/**
 * How a group ("Pay for both") refund is booked onto the linked enrollments.
 * The Swish payout always goes back to the original payer; this only chooses
 * which enrollment(s) receive the negative `Payment` row.
 */
export enum GroupRefundAllocation {
  Payer = 'payer',
  Both = 'both'
}

export type PayForGroupRequest = {
  enrollmentGroupId: string
  paymentMethod?: PaymentMethod
}

export type PayForGroupSuccess = {
  ok: true
  clientSecret: string
  paymentIntentId: string
  splits: PayForGroupSplit[]
}

export type PayForGroupErrorCode =
  | 'group_not_found'
  | 'pay_for_both_disabled'
  | 'mixed_client'
  | 'mixed_currency'
  | 'amount_must_be_positive'
  | 'unknown'

export type PayForGroupFailure = {
  ok: false
  errorCode: PayForGroupErrorCode
  message: string
}

export type PayForGroupResponse = PayForGroupSuccess | PayForGroupFailure

export type PayForGroupWithUserCreditRequest = {
  enrollmentGroupId: string
  payerEnrollmentId: string
}

export type PayForGroupWithUserCreditSuccess = {
  ok: true
  splits: PayForGroupSplit[]
  totalAmount: number
}

export type PayForGroupWithUserCreditErrorCode = PayForGroupErrorCode | 'insufficient_credit' | 'payer_not_found'

export type PayForGroupWithUserCreditFailure = {
  ok: false
  errorCode: PayForGroupWithUserCreditErrorCode
  message: string
}

export type PayForGroupWithUserCreditResponse = PayForGroupWithUserCreditSuccess | PayForGroupWithUserCreditFailure

export type PayForGroupWithEpassiRequest = {
  enrollmentGroupId: string
  payerEnrollmentId: string
  /** `window.location.origin` of the page starting the payment; absent falls back to production. */
  returnOrigin?: string
}

export type PayForGroupWithEpassiSuccess = {
  ok: true
  /** Form parameters for the frontend to build and POST the ePassi HTML form. */
  response: import('./EpassiTypes').EpassiInitiatePaymentResponse
  splits: PayForGroupSplit[]
  totalAmount: number
}

export type PayForGroupWithEpassiErrorCode = PayForGroupErrorCode | 'payer_not_found' | 'epassi_not_configured'

export type PayForGroupWithEpassiFailure = {
  ok: false
  errorCode: PayForGroupWithEpassiErrorCode
  message: string
}

export type PayForGroupWithEpassiResponse = PayForGroupWithEpassiSuccess | PayForGroupWithEpassiFailure

export type PayForGroupWithSwishRequest = {
  enrollmentGroupId: string
  payerEnrollmentId: string
  payerPhone?: string
  usePhoneFromEnrollment?: boolean
}

export type PayForGroupWithSwishSuccess = {
  ok: true
  instructionUUID: string
  token?: string
  splits: PayForGroupSplit[]
  totalAmount: number
}

export type PayForGroupWithSwishErrorCode = PayForGroupErrorCode | 'payer_not_found' | 'swish_not_configured'

export type PayForGroupWithSwishFailure = {
  ok: false
  errorCode: PayForGroupWithSwishErrorCode
  message: string
}

export type PayForGroupWithSwishResponse = PayForGroupWithSwishSuccess | PayForGroupWithSwishFailure

export type PayForGroupWithVippsRequest = {
  enrollmentGroupId: string
  payerEnrollmentId: string
  payerPhoneNumber?: string
  /** `window.location.origin` of the page starting the payment; absent falls back to production. */
  returnOrigin?: string
}

export type PayForGroupWithVippsSuccess = {
  ok: true
  /** Vipps ePayment reference (also used for checkout sessions when applicable). */
  reference: string
  /** Checkout-only: frontend token + checkout URL for the Vipps SDK redirect. */
  frontend?: {
    token: string
    checkoutFrontendUrl: string
  }
  splits: PayForGroupSplit[]
  totalAmount: number
}

export type PayForGroupWithVippsErrorCode = PayForGroupErrorCode | 'payer_not_found' | 'vipps_not_configured'

export type PayForGroupWithVippsFailure = {
  ok: false
  errorCode: PayForGroupWithVippsErrorCode
  message: string
}

export type PayForGroupWithVippsResponse = PayForGroupWithVippsSuccess | PayForGroupWithVippsFailure

export type CreatePaymentIntentResponse = {
  clientSecret: string | null
  amount: number
  currency: string
}

export type CloudFunctionErrorResponse = {
  isError: boolean
  source: string
  message: string
  errorCode?: string // Optional error code for client-side handling (e.g., 'main_user_already_paid')
}

export type GetActivePeriodsForUserRequest = {
  phone: string // phone is unique for a user
  clientId: string
}

export type GetActivePeriodsForUserResponse = string[]

export type GetUserPreviousDiscountsRequest = {
  phone: string
  clientId: string
}

export type GetUserPreviousDiscountsResponse = {
  userEnrolledActivityIds: string[]
  userEnrolledPeriodIds: string[] // period IDs from all historical enrollments (for continuation discount eligibility)
  /** Distinct prior activities with their period, for period-mode bulk discount slot counting. */
  userEnrolledActivityPeriods: UserEnrolledActivityPeriod[]
  usedContinuationDiscountIds: string[]
  usedContinuationDiscountActivities: UsedContinuationDiscountActivity[]
}

export type GetCreditForUserRequest = {
  phone: string
  clientId: string
  enrollmentId?: string
}

export type GetCreditForUserResponse = {
  userId: string
  clientId: string
  credit: number
}

export type PayWithUserCreditRequest = {
  phone: string
  clientId: string
  amount: number
  enrollmentId: string
  /** When set, credit is debited from this enrollment's mainUser (payer), not the payment target enrollment. */
  payerEnrollmentId?: string
}

export type CreateHelpDancerForClientRequest = {
  clientId: string
  name: string
  email: string
  phone: string
  level?: HelpDancerLevel
  approvedActivityIds?: string[]
  comment?: string
}

export type CreateHelpDancerForClientResponse = {
  enrollmentUserId: string
}

export type SetHelpDancerForEnrollmentUserRequest = {
  clientId: string
  enrollmentUserId: string
  enabled: boolean
  level?: HelpDancerLevel
  approvedActivityIds?: string[]
  comment?: string
}

export type SetHelpDancerForEnrollmentUserResponse = {
  enrollmentUserId: string
  enabled: boolean
}

export type SyncEnrollmentUserDetailsRequest = {
  clientId: string
  enrollmentUserId: string
  updates: Partial<Pick<EnrollmentUser, 'name' | 'email' | 'ssn' | 'address'>>
}

export type SyncEnrollmentUserDetailsResponse = {
  user: EnrollmentUser
  updatedEnrollmentIds: string[]
}

export enum ChangeEnrollmentUserPhoneOutcome {
  Updated = 'updated',
  Conflict = 'conflict'
}

export type ChangeEnrollmentUserPhoneRequest = {
  clientId: string
  enrollmentUserId: string
  newPhone: string
}

export type ChangeEnrollmentUserPhoneConflictUser = Pick<EnrollmentUser, 'id' | 'name' | 'email' | 'phone'>

export type ChangeEnrollmentUserPhoneResponse =
  | { outcome: ChangeEnrollmentUserPhoneOutcome.Updated; user: EnrollmentUser }
  | { outcome: ChangeEnrollmentUserPhoneOutcome.Conflict; existingUser: ChangeEnrollmentUserPhoneConflictUser }

export type MergeEnrollmentUsersRequest = {
  clientId: string
  sourceEnrollmentUserId: string
  targetEnrollmentUserId: string
}

export enum MergeEnrollmentUsersOutcome {
  Merged = 'merged',
  CoupleConflict = 'couple_conflict'
}

export type MergeEnrollmentUsersResponse =
  | { outcome: MergeEnrollmentUsersOutcome.Merged; user: EnrollmentUser }
  | { outcome: MergeEnrollmentUsersOutcome.CoupleConflict }

export type EnrollmentActivityCheckinRequest = {
  enrollmentId: string
  activityId: string
  occurenceNo: number
  userId: string
  newState: boolean
}

export type GetEmailRecipientsForActivitiesRequest = {
  clientId: string
  activityIds: string[]
  statsOnly?: boolean
}

export type GetEmailRecipientsForActivitiesResponseItem = {
  id: string
  name: string
  email: string
  activities?: {
    activityId: string
    activityName: string
    status: string
  }[]
  isSuppressed?: boolean
  suppressionEvents?: SuppressionEvent[]
}

export type GetEmailRecipientsForActivitiesResponse = {
  recipients: GetEmailRecipientsForActivitiesResponseItem[]
  stats: {
    totalUniqueUsers: number
    perActivity: Record<string, number>
  }
}

export type AddSsnForEnrollmentUserRequest = {
  clientId: string
  enrollmentId: string
  userType: 'mainUser' | 'partnerUser'
  ssn: string
}

export type UpdateEnrollmentCommentRequest = {
  clientId: string
  enrollmentId: string
  comment: string
}

export type AddSsnForDropinTicketRequest = {
  clientId: string
  dropinTicketId: string
  ssn: string
}

export type AddSsnForPrivateLessonOrderRequest = {
  clientId: string
  orderId: string
  ssn: string
}

export type CreateMailingRequest = {
  clientId: string
  subject: string
  replyToEmail: string
  replyToName: string
  body: string
  recipients: string[]
  activities: string[]
  overrideEmail?: string
  sendCopyToEmail?: string
  emailService: EmailService
  scheduleTime?: string // UTC ISO 8601 timestamp for scheduled sending
}

export type onTaskMailingItemRequest = {
  mailingId: string
  enrollmentUserId: string
  overrideEmail?: string
}

export type onTaskMailingPostmarkBatchRequest = {
  mailingId: string
  recipientUserIds: string[]
  overrideEmail?: string
}

export type onTaskMailingScheduledTriggerRequest = {
  mailingId: string
}

/**
 * Payload for the delayed per-mailing summary task. Deliberately carries nothing but the id:
 * the task runs ~10 minutes after fan-out, so anything else would be read from a stale snapshot.
 */
export type onTaskMailingSummaryRequest = {
  mailingId: string
}

export type CancelMailingRequest = {
  mailingId: string
}

export type onTaskRegistrationNotificationsAfterSaveRequest = {
  enrollmentId: string
  env: string
}

export type OnTaskFreeDropinReconciliationRequest = {
  clientId: string
  enrollmentId: string
}

export enum FreeDropinPreviewUserRole {
  Main = 'main',
  Partner = 'partner'
}

export type FreeDropinRewardPreviewUserRequest = {
  role: FreeDropinPreviewUserRole
  phoneNumber: string
}

export type FreeDropinRewardPreviewRequest = {
  clientId: string
  users: FreeDropinRewardPreviewUserRequest[]
  selectedRegistrations: QualifyingRegistration[]
  ruleRevisions: { ruleId: string; revision: number }[]
}

export type FreeDropinRewardPreviewUserResponse = {
  role: FreeDropinPreviewUserRole
  priorQualificationCounts: FreeDropinPriorQualificationCount[]
  /** True when this phone resolves to an enabled help dancer for the client. */
  isHelpDancer?: boolean
}

export type FreeDropinPriorQualificationCount = {
  ruleId: string
  ruleRevision: number
  periodKey: string
  qualifyingCount: number
}

export type FreeDropinRewardPreviewResponse = {
  users: FreeDropinRewardPreviewUserResponse[]
}

export type GetFreeDropinRuleUsageRequest = {
  clientId: string
  periodKey: string
  ruleIds?: string[]
}

export type FreeDropinRuleRevisionUsage = {
  ruleId: string
  ruleRevision: number
  periodKey: string
  grantCount: number
  entitledTickets: number
  availableTickets: number
}

export type FreeDropinLogicalRuleUsage = Omit<FreeDropinRuleRevisionUsage, 'ruleRevision'>

export type FreeDropinGrantPeriodKeyCount = {
  periodKey: string
  grantCount: number
}

export type GetFreeDropinRuleUsageResponse = {
  periodKey: string
  revisions: FreeDropinRuleRevisionUsage[]
  logicalRules: FreeDropinLogicalRuleUsage[]
  /**
   * Every `periodKey` that actually has grants for this client, regardless of the requested period.
   * Grants are keyed by `activity.periodId || activity.period` (a Period *document id*, or a legacy
   * free-text period string), so a period picker can easily request a key nothing was granted under.
   * Surfacing the real keys turns that silent empty result into an actionable one.
   */
  grantPeriodKeys: FreeDropinGrantPeriodKeyCount[]
}

export type SignInFromAdminAppQueryParams = {
  authToken?: string
  sessionId?: string
}

export type CreateEarlyBirdBookingRequest = {
  activityId: string
  enrollmentUser: EnrollmentUserRequest
  registrationType: 'single' | 'couple'
  region: string
  price: number
  pricingConfigId: string
}

// Drop-in ticket request/response types
export type CreateDropinTicketRequest = {
  idToken: string
  clientId: string
  packageId: string
  paymentMethod: PaymentMethod
}

export type CreateDropinTicketResponse = {
  ticket: DropinTicket
}

export type CreateDropinTicketForPhoneRequest = Omit<CreateDropinTicketRequest, 'idToken'> & {
  phoneNumber: string
}

export type CreateDropinTicketAndInitiateSwishPaymentRequest = {
  phoneNumber: string
  name: string
  email: string
  clientId: string
  packageId: string
  isStudent?: boolean
}

export type CreateDropinTicketAndInitiateSwishPaymentResponse = {
  ticketId: string
  paymentTransactionId: string
  instructionUUID: string
  token?: string
}

export type UseDropinTicketRequest = {
  idToken: string
  ticketId: string
  activityId?: string
  locationId?: string
  deviceInfo?: string
}

export type UseDropinTicketResponse = {
  success: boolean
  remainingTickets: number
  log: DropinTicketLog
}

export type GetPaymentTransactionByDropinTicketIdRequest = {
  idToken: string
  dropinTicketId: string
}

export type GetPaymentTransactionByDropinTicketIdResponse = {
  paymentTransaction: PaymentTransaction | null
}

export type CreateDropinTicketManualRequest = {
  clientId: string
  packageId: string
  phone: string
  name: string
  email: string
  price?: number // in cents; if omitted, defaults to package totalPrice
  comment?: string // internal admin note; readable via Firestore SDK — do not store sensitive data
  paymentMethod?: PaymentMethod // defaults to BankTransfer
}

export type CreateDropinTicketManualResponse = {
  ticket: DropinTicket
}

export type SoftDeleteDropinTicketRequest = {
  ticketId: string
}

export type SoftDeleteDropinTicketResponse = {
  success: boolean
  ticket: DropinTicket
}

export type UpdateDropinTicketRequest = {
  ticketId: string
  packageId?: string
  ticketCount?: number
  ticketsUsed?: number
  validUntil?: string
  status?: DropinTicket['status']
  price?: number
  comment?: string // internal admin note; readable via Firestore SDK — do not store sensitive data; pass empty string to clear
  paymentMethod?: PaymentMethod
}

export type UpdateDropinTicketResponse = {
  success: boolean
  ticket: DropinTicket
}

export type RefundDropinTicketRequest = {
  ticketId: string
  amount: number // in cents (öre)
  comment?: string
  refundReason?: string
}

export type RefundDropinTicketResponse = {
  success: boolean
  ticket: DropinTicket
  refundPaymentRef?: string // payment provider reference
}

export type ResendDropinPaymentConfirmationRequest = {
  ticketId: string
  clientId: string
}

export type ResendDropinPaymentConfirmationResponse = {
  ok: true
}

export type RefundStripePaymentForEnrollmentRequest = {
  enrollmentId: string
  paymentIntentId: string // which Stripe payment to refund
  amount: number // in öre/cents
  comment?: string // optional; stored in Stripe refund metadata + our payment log
}

export type RefundStripePaymentForEnrollmentResponse = {
  success: boolean
  refundPaymentRef?: string
}

/** Error shape for the reception Stripe Terminal walk-in callables — parallel to Swish's `SwishResponseError`. */
export type StripeWalkInResponseError = {
  isError: true
  source: string
  message: string
  /** e.g. READER_BUSY | READER_OFFLINE | READER_NOT_FOUND | ALREADY_PAID | PHONE_REQUIRED | ... */
  errorCode: string
}

/** Callable `createReceptionStripeWalkInSession` (Stripe Terminal S700 reception card walk-in). */
export type CreateReceptionStripeWalkInSessionRequest = {
  clientId: string
  activityId: string
  /** Walk-in: price before student discount. Never trusted as the final charge amount — recalculated server-side. */
  baseAmountInCents: number
  isStudent: boolean
  /** Number of tickets (default 1); allowed >1 only when the activity supports multi-ticket walk-in. */
  ticketQuantity?: number
  /** Required unless the client's `receptionStripeWalkInAllowAnonymous` toggle is on (validated server-side). */
  phone?: string
}

export type CreateReceptionStripeWalkInSessionResponse = {
  sessionDocId: string
  paymentIntentId: string
}

/** Callable `cancelReceptionStripeWalkInSession`. `clientId` is required so authorization can be client-scoped. */
export type CancelReceptionStripeWalkInSessionRequest = {
  clientId: string
  sessionDocId: string
}

export type CancelReceptionStripeWalkInSessionResponse = {
  cancelled: boolean
  /** True when the PaymentIntent had already succeeded — cancel was refused, not a failure (req. 15). */
  alreadyPaid?: boolean
}

/**
 * Callable `createReceptionStripeEnrollmentPayment` — Stripe Terminal (S700) card payment against an
 * **existing** enrollment's outstanding balance, from Reception's Trigger Pay (COU-325).
 *
 * Unlike the walk-in rail there is no session document: the enrollment and payer are already known,
 * so `{ enrollmentId, clientId, userId }` travels in the PaymentIntent metadata and the existing
 * enrollment webhook branch settles it. Metadata deliberately carries **no `type`** — its absence is
 * what routes the event to that branch.
 */
export type CreateReceptionStripeEnrollmentPaymentRequest = {
  clientId: string
  enrollmentId: string
  payFor: PayFor
  /**
   * Optional partial amount. Never trusted — always validated server-side against the remaining
   * balance, using the same rules as the Swish rail.
   */
  amountInCents?: number
  /**
   * Client-minted uuid, one per user-initiated attempt, used as the Stripe idempotency key.
   * A transport retry (double-click, callable retry) reuses it and collapses to one PaymentIntent;
   * a deliberate retry after a decline is a new attempt with a new id, and so a new charge.
   */
  attemptId: string
  /**
   * Set by Reception's "Try anyway" after a `READER_APPEARS_OFFLINE` refusal, to skip the reader
   * status pre-check for this attempt.
   *
   * The pre-check reads `reader.status`, which Stripe documents as networking state it explicitly
   * does not recommend gating payments on. Honouring it without an escape hatch would let a stale
   * field block a working terminal, so the refusal is always overridable by the operator standing
   * in front of the reader — who can see whether it is actually on.
   */
  skipReaderStatusCheck?: boolean
}

export type CreateReceptionStripeEnrollmentPaymentResponse = {
  paymentIntentId: string
  /** The server-computed amount actually pushed to the reader, in minor units. */
  amountInCents: number
}

/** Callable `cancelReceptionStripeEnrollmentPayment`. `clientId` is required so authorization can be client-scoped. */
export type CancelReceptionStripeEnrollmentPaymentRequest = {
  clientId: string
  paymentIntentId: string
}

export type CancelReceptionStripeEnrollmentPaymentResponse = {
  cancelled: boolean
  /** True when the PaymentIntent had already succeeded — cancel was refused, not a failure. */
  alreadyPaid?: boolean
}

// Reception (admin staff) drop-in ticket helper types
export type GetDropinTicketActivationStatusRequest = {
  ticketId: string
  clientId: string
}

export type GetDropinTicketActivationStatusResponse = {
  lastConsumeLog: DropinTicketLog | null
  /** When the ticket exists, mirrors Firestore; use Active to detect payment confirmed (parallel to stripe_payment_intent_status). */
  ticketStatus?: DropinTicketStatus
}

export type ConsumeDropinTicketForReceptionRequest = {
  ticketId: string
  /** Activity where ticket is used. Required when activity has occasions. */
  activityId?: string
  /** Occasion (OccationInstance.id) where ticket is used. Required for activities with occasions. */
  occasionId?: string
  /**
   * Multiple occasions: attribution only (one clip on this ticket for the whole batch).
   * Takes precedence over activityId/occasionId.
   */
  occasions?: { activityId: string; occasionId?: string }[]
  /**
   * How many clips to consume on this ticket (same `occasions` attribution repeated per clip). Default 1. Max 12.
   */
  participantCount?: number
}

export type ConsumeDropinTicketForReceptionResponse = {
  success: boolean
  remainingTickets: number
  ticket: DropinTicket
}

export type GetDropinCheckinsForActivityRequest = {
  clientId: string
  activityId: string
}

export type GetDropinCheckinsForActivitiesRequest = {
  clientId: string
  activityIds: string[]
}

/**
 * A single "drop-in usage" row shown in the Activity → Drop-in clip usage tab.
 * Covers two sources:
 *   - A clip consumed from a DropinTicket (punched card)
 *   - A Swish walk-in payment that created a new enrollment at the door (`isWalkIn=true`)
 *
 * We keep the "checkin" word out of the type name to avoid confusion with enrollment check-ins;
 * wire field names on the CF response (`checkins`, `occasionCheckins`) are intentionally stable
 * to avoid a coordinated deploy.
 */
export type DropinClipUsageEntry = {
  enrollmentUserId: string
  userName: string
  ticketId: string
  timestamp: string
  /** Set when the row comes from a reception Swish walk-in, not a drop-in ticket consume */
  isWalkIn?: boolean
  /** Phone for walk-in rows when available */
  phone?: string
  /**
   * Walk-in only: paid amount for this row (öre). When `walkInTicketQuantity` > 1, this is an equal split of
   * the session total (`session.amount / qty`, rounded) so each expanded row has a sensible display value.
   */
  walkInPaidAmountCents?: number
  /** Walk-in only: ISO currency code from the Swish session (e.g. SEK) */
  walkInCurrency?: string
  /**
   * Walk-in only: `Payment.paymentRef` for this row's walk-in payment (Stripe Terminal
   * `stripePaymentIntentId` when present, else the Swish `instructionUUID`) — lets the UI look up
   * the matching `Payment` on the enrollment to preview/download its receipt.
   */
  walkInPaymentRef?: string
}

export type DropinClipUsageForOccasion = {
  occasionId?: string
  occasionStartDate?: string
  /** Stable wire name: `checkins`. Internally this is a list of `DropinClipUsageEntry`. */
  checkins: DropinClipUsageEntry[]
}

export type GetDropinCheckinsForActivityResponse = {
  /** Stable wire name: `occasionCheckins`. Internally these are clip-usage rows, not enrollment check-ins. */
  occasionCheckins: DropinClipUsageForOccasion[]
}

export type GetDropinCheckinsForActivitiesResponse = {
  byActivityId: Record<string, GetDropinCheckinsForActivityResponse>
}

export type GetCurrentEnrollmentUserRequest = {
  idToken: string
}

export type GetCurrentEnrollmentUserResponse = {
  isError: boolean
  userId?: string
  message?: string // set if isError=true
}

export type OnSyncPermissionsForUserRequest = {
  uid: string
}

export type OnSyncPermissionsForUserResponse = {
  status: string
  message: string
}

/** Empty payload; superadmin authorization is enforced in the function. */
export type OnSyncPermissionsRequest = Record<string, never>

export type OnSyncPermissionsResponse = {
  status: string
  message: string
}

export type CheckDiscountCodeRequest = {
  clientId: string
  code: string
}

export type ConvertHardCoupleToSoftCoupleRequest = {
  enrollmentId: string
}

export type ConvertHardCoupleToSoftCoupleResponse = {
  mainEnrollmentId: string
  partnerEnrollmentId: string
  success: boolean
}

export type ValidateClientEmailConfigRequest = {
  fromEmail: string
  fromName: string
}

export type ValidateClientEmailConfigResponse = {
  emailConfig: {
    fromEmail: string
    fromName: string
    trustedBy: {
      sendgrid: boolean
      postmark: boolean
    }
  }
  defaultEmail: string
}

export type UpdateClientEmailConfigRequest = {
  clientId: string
  fromEmail: string
  fromName: string
}

export type UpdateClientEmailConfigResponse = {
  success: boolean
  emailConfig: {
    fromEmail: string
    fromName: string
    trustedBy: {
      sendgrid: boolean
      postmark: boolean
    }
  }
}

export type InviteAdminUserRequest = {
  email: string
  clientId: string
  role: 'admin' | 'user' | 'developer'
  activityIds?: string[]
  name: string
}

export type InviteAdminUserResponse = {
  success: boolean
  message: string
  userId: string
}

export type UpdateUserClientIdsRequest = {
  userId: string
  clientIds: string[]
}

export type UpdateUserClientIdsResponse = {
  success: boolean
  userId: string
  message?: string
}

export type UpdateUserRoleRequest = {
  userId: string
  roles: UserRole[]
}

export type UpdateUserRoleResponse = {
  success: boolean
  userId: string
  message?: string
}

// AI Configuration Query Agent Types
export interface FilterCondition {
  field: string
  operator: 'exists' | '==' | '!=' | '>' | '<' | '>=' | '<=' | 'contains' | 'in' | 'array-length'
  value: any
}

export interface QueryClientConfigurationsRequest {
  query: string
  includeEnrollments?: boolean
}

export interface QueryClientConfigurationsResponse {
  results: any[] // Client objects with optional extensions
  summary: {
    total: number
    matched: number
    query: string
    interpretation: string
  }
  filterLogic: FilterCondition[]
}

// =============================================================================
// SMS Billing
// =============================================================================

export type AddSmsTopUpRequest = {
  clientId: string
  amount: number
  currency: string
  paymentMethod: 'manual' | 'stripe'
  paymentId?: string
  purchasedBy: string
}

export type UpdateSmsTopUpStatusRequest = {
  clientId: string
  smsTopUpId: string
  status: 'canceled' | 'failed'
}

export type SmsTopUpActionResponse = {
  success: boolean
  newAllowance: number
  topUpId: string
}

/** SuperAdmin last-month invoicing: SMS bank + last-month billable parts per client. */
export type GetLastMonthInvoicingSmsStatsRequest = {
  clientIds: string[]
}

export type LastMonthInvoicingSmsStatsRow = {
  clientId: string
  /** allowance − all-time billable parts; null when unlimited / no SMS config */
  remaining: number | null
  unlimited: boolean
  /** Billable SMS parts in the last calendar month (Europe/Stockholm) */
  smsPartsLastMonth: number
}

export type GetLastMonthInvoicingSmsStatsResponse = {
  rows: LastMonthInvoicingSmsStatsRow[]
  monthKey: string
}

export type SendCustomSmsRequest = {
  clientId: string
  enrollmentUserIds: string[]
  message: string
}

export type SendCustomSmsResponse = {
  sent: number
  failed: number
  skipped: number
  creditsUsed: number
  remaining: number
}

export type UpdateSmsConfigRequest = {
  clientId: string
  lowBalanceThreshold?: number // undefined = disabled, number = enabled with threshold
  enabledEmailTypes?: CommunicationLogEvent[] // Which email types trigger SMS
}

export type UpdateSmsConfigResponse = {
  success: boolean
}

export type CreateSmsStripePaymentIntentRequest = {
  clientId: string
  amount: number // Number of SMS credits to purchase
  priceInCents: number // Price in cents (öre) - passed from UI where packages are defined
  purchasedBy: string
}

export type CreateSmsStripePaymentIntentResponse = {
  clientSecret: string | null
  smsTopUpId: string
  amount: number // Amount in cents (öre)
  currency: Currency // Currency from client config (e.g., 'sek', 'nok')
}

export type GenerateActivityDescriptionRequest = {
  activityId: string
  clientId: string
  adminPrompt: string
  /** Present when refining an existing description instead of generating from scratch. */
  currentDescription?: string
  /** Instructions for how to improve the current description. */
  refinePrompt?: string
  /** When true, only restructure formatting; keep wording unchanged. */
  fixFormatting?: boolean
}

export type GenerateActivityDescriptionResponse = {
  description: string
}

export type CreateStripePaymentIntentForDropinTicketRequest = {
  clientId: string
  packageId: string
  name: string
  email: string
  phoneNumber: string
  isStudent?: boolean
}

export type CreateStripePaymentIntentForDropinTicketResponse = {
  clientSecret: string
  ticketId: string
  amount: number
  currency: string
}

export type CreateStripePaymentIntentForPrivateLessonOrderRequest = {
  clientId: string
  orderId: string
}

export type GetCustomerMetricsRequest = {
  clientId: string
}

export type GetCustomerMetricsResponse = UserMetricsData

/** Cloud Task payload for recomputing one client's cached customer metrics (COU-338). */
export type OnTaskRecomputeCustomerMetricsRequest = {
  clientId: string
}

/**
 * Cloud Task payload for one client's daily automated reminders.
 *
 * `today` is resolved once by the scheduler and passed down rather than recomputed per task, so a
 * fan-out that straddles midnight Europe/Stockholm (or a task retried into the next day) still
 * targets the day the run was scheduled for. Every reminder-due comparison is date-based, so a task
 * silently shifting by one day would send the wrong day's reminders.
 */
export type OnTaskAutomatedRemindersForClientRequest = {
  clientId: string
  /** YYYY-MM-DD, Europe/Stockholm, as of when the scheduler fired. */
  today: string
}

/**
 * Payload sent from a frontend to the sendSlackNotification proxy.
 * text: message body (1–4000 chars at the type level; the server zod schema
 *       enforces the stricter runtime bound of 1–3000 after trimming).
 * channel: must be a member of the SlackChannels enum.
 */
export type SendSlackNotificationRequest = {
  text: string
  channel: SlackChannels
}

/**
 * Proxy result. `success` is true only when the message was forwarded
 * (or console-logged in dev mode, which is indistinguishable by design).
 * Never carries credential presence/absence or internal error detail.
 */
export type SendSlackNotificationResponse = {
  success: boolean
}

export type CheckUnfinishedEnrollmentRequest = {
  clientId: string
  activityId: string
  phoneNumber: string
  partnerPhoneNumber?: string
}

export type CheckUnfinishedEnrollmentResponse = Enrollment | null

export type GetSuppressionInfoForEmailsRequest = {
  emails: string[]
}

export type GetSuppressionInfoForEmailsResponse = Record<string, SuppressionInfo>

export type UpsertClientEmailFlowTemplateRequest = {
  clientId: string
  flowKey: EmailFlowKey
  enabled: boolean
  sections: Partial<Record<ClientEmailFlowSectionKey, string>>
  subject?: string
}

export type UpsertClientEmailFlowTemplateResponse = { ok: true } | CloudFunctionErrorResponse

export type PreviewClientEmailFlowRequest = {
  clientId: string
  flowKey: EmailFlowKey
  /** Sample order for PrivateLessonPurchaseSchoolNotify. */
  privateLessonOrderId?: string
  /** Sample enrollment for ActivityStartsSoon / PaymentReminder. */
  enrollmentId?: string
  /** Sample activity for ActivityStartsSoon / PaymentReminder. */
  activityId?: string
  /** When set, used instead of the saved template system section (draft preview). */
  systemSectionHtml?: string
  /** When set, used instead of the saved template client section (draft preview). */
  clientSectionHtml?: string
  /** When set, used instead of the saved template subject (draft preview). */
  subject?: string
}

export type PreviewClientEmailFlowResponse = ({ ok: true } & RenderedClientEmail) | CloudFunctionErrorResponse

export type SendTestClientEmailFlowRequest = {
  clientId: string
  flowKey: EmailFlowKey
  /** Destination for the test send (admin-entered). */
  toEmail: string
  /** Sample order for PrivateLessonPurchaseSchoolNotify (same as preview). */
  privateLessonOrderId?: string
  /** Sample enrollment for ActivityStartsSoon / PaymentReminder. */
  enrollmentId?: string
  /** Sample activity for ActivityStartsSoon / PaymentReminder. */
  activityId?: string
  /** When set, used instead of the saved template system section (draft test). */
  systemSectionHtml?: string
  /** When set, used instead of the saved template client section (draft test). */
  clientSectionHtml?: string
  /** When set, used instead of the saved template subject (draft test). */
  subject?: string
}

export type SendTestClientEmailFlowResponse = { ok: true; to: string; subject: string } | CloudFunctionErrorResponse

export type ListClientEmailTemplateSendsRequest = {
  clientId: string
  flowKey: EmailFlowKey
  limit?: number
  search?: string
}

export type ListClientEmailTemplateSendsResponse =
  { ok: true; totalCount: number; sends: ClientEmailTemplateSend[] } | CloudFunctionErrorResponse

export type GetClientEmailTemplateSendRequest = {
  clientId: string
  sendId: string
}

export type GetClientEmailTemplateSendResponse =
  { ok: true; send: ClientEmailTemplateSend } | CloudFunctionErrorResponse

export type StartPortalViewAsRequest = {
  enrollmentUserId: string
  /** School portal to preview. Omit or empty for the unified (all schools) portal. */
  clientId?: string
}

export type StartPortalViewAsResponse = {
  ticketId: string
  customToken: string
  expiresAt: string
  enrollmentUserId: string
  clientId?: string
}

export type RedeemPortalViewAsRequest = {
  ticketId: string
}

export type RedeemPortalViewAsResponse = {
  ticketId: string
  enrollmentUserId: string
  expiresAt: string
  clientId?: string
}

export type GetPortalViewAsSessionRequest = {
  ticketId: string
}

export type GetPortalViewAsSessionResponse = {
  ticketId: string
  enrollmentUserId: string
  expiresAt: string
  clientId?: string
}

export type EndPortalViewAsRequest = {
  ticketId: string
}

export type EndPortalViewAsResponse = {
  ticketId: string
  ended: boolean
}
